/**
 * Seedable RNG and 6-hex seed utilities.
 *
 * Seeds are 6 hexadecimal characters (e.g. "a4f3b9"), shown in the UI and
 * embedded in exported file names so any individual KV can be re-rendered
 * deterministically (SPEC §11 — Seedable Randomness).
 */

const HEX_ALPHABET = '0123456789abcdef';

/**
 * mulberry32 — fast, deterministic 32-bit PRNG.
 * Produces a function that returns a number in [0, 1).
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function rng(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string seed (e.g. "a4f3b9") to a 32-bit integer for mulberry32. */
export function hashSeed(seed: string): number {
  let h = 2166136261 >>> 0; // FNV-1a offset basis
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Build an RNG directly from a 6-hex seed string. */
export function rngFromSeed(seed: string): () => number {
  return mulberry32(hashSeed(seed));
}

/**
 * Generate a fresh 6-hex seed using crypto.getRandomValues when available
 * and falling back to Math.random otherwise.
 */
export function generateSeed(): string {
  const cryptoObj =
    typeof globalThis !== 'undefined' && 'crypto' in globalThis
      ? (globalThis as { crypto?: Crypto }).crypto
      : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const buf = new Uint8Array(3);
    cryptoObj.getRandomValues(buf);
    let s = '';
    for (let i = 0; i < buf.length; i++) {
      const byte = buf[i] ?? 0;
      s += HEX_ALPHABET[(byte >> 4) & 0xf];
      s += HEX_ALPHABET[byte & 0xf];
    }
    return s;
  }
  let s = '';
  for (let i = 0; i < 6; i++) {
    s += HEX_ALPHABET[Math.floor(Math.random() * 16)];
  }
  return s;
}

/** Validate a 6-hex string. */
export function isValidSeed(seed: string): boolean {
  return /^[0-9a-f]{6}$/i.test(seed);
}
